import { forwardRef, useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cx } from '@shared/lib/cx'
import { FieldFeedback } from '../Field/FieldFeedback'
import { resolveFieldFeedback } from '../Field/field-feedback'
import type { FieldStatus } from '../Field/field-feedback'
import { control, field, iconSlot, inputEl, label as labelClass, labelInvalid } from './Input.css'

export type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string
  helperText?: string
  error?: string
  status?: FieldStatus
  message?: string
  leftIcon?: ReactNode
  rightSlot?: ReactNode
  belowSlot?: ReactNode
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    helperText,
    error,
    status,
    message,
    leftIcon,
    rightSlot,
    belowSlot,
    className,
    id,
    ...rest
  },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const feedbackId = `${inputId}-feedback`
  const feedback = resolveFieldFeedback({ error, helperText, message, status })
  const describedBy = cx(rest['aria-describedby'], feedback.message && feedbackId)

  return (
    <div className={cx(field, className)}>
      {label ? (
        <label htmlFor={inputId} className={cx(labelClass, feedback.invalid && labelInvalid)}>
          {label}
        </label>
      ) : null}
      <div className={control({ invalid: feedback.invalid, disabled: Boolean(rest.disabled) })}>
        {leftIcon ? <span className={iconSlot}>{leftIcon}</span> : null}
        <input
          {...rest}
          id={inputId}
          ref={ref}
          className={inputEl}
          aria-invalid={feedback.invalid}
          aria-describedby={describedBy || undefined}
        />
        {rightSlot ? <span className={iconSlot}>{rightSlot}</span> : null}
      </div>
      <FieldFeedback id={feedbackId} status={feedback.status}>
        {feedback.message}
      </FieldFeedback>
      {belowSlot}
    </div>
  )
})
